// src/components/programs/JobOrders/CreateEditJobOrderModal.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Grid,
    IconButton
} from "@mui/material";
import { Close, ReceiptLong } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import useCreateJobOrder from "../../../hooks/jobOrders/useCreateJobOrder";
import useUpdateJobOrder from "../../../hooks/jobOrders/useUpdateJobOrder";

const getLocalizedText = (val, currentLang = "en") => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "number") return String(val);
    if (typeof val === "object") {
        const isAr = currentLang?.startsWith("ar");
        const primary = isAr ? "ar" : "en";
        const secondary = isAr ? "en" : "ar";
        if (val[currentLang] && typeof val[currentLang] === "string") return val[currentLang];
        if (val[primary] && typeof val[primary] === "string") return val[primary];
        if (val[secondary] && typeof val[secondary] === "string") return val[secondary];
        if (val.name) return getLocalizedText(val.name, currentLang);
        if (val.title) return getLocalizedText(val.title, currentLang);
        return "";
    }
    return String(val);
};

const CreateEditJobOrderModal = ({ open, onClose, program, occurrence, jobOrder, onSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng?.startsWith("ar");

    const isEdit = !!jobOrder;
    const isProgramSettled = program?.status === 'settled' || program?.is_settled;
    const isArchived = jobOrder?.status === 'archived';
    const isReadOnly = isProgramSettled || isArchived;

    const { createJobOrderMutate, isPending: isCreating } = useCreateJobOrder();
    const { updateJobOrderMutate, isPending: isUpdating } = useUpdateJobOrder();

    const [paxBreakdown, setPaxBreakdown] = useState([]);
    const [notes, setNotes] = useState({ ar: "", en: "" });
    const [pickupLocation, setPickupLocation] = useState({ ar: "", en: "" });
    const [dropOffLocation, setDropOffLocation] = useState({ ar: "", en: "" });
    const [cashOnTour, setCashOnTour] = useState("");

    // Identify whether this service is a tour or transfer (supported in both Create and Edit)
    const isTourOrTransfer = useMemo(() => {
        const opDetails = occurrence?.operational_details || jobOrder?.operational_details || {};
        const opType = (opDetails.product_type || "").toLowerCase();
        if (opType === "tour" || opType === "transfer") return true;
        if (opType === "hotel" || opType === "flight") return false;

        // Check if hotel or flight details exist
        if (opDetails.hotel || jobOrder?.operational_details?.hotel || occurrence?.hotel_details || occurrence?.hotel_id || occurrence?.hotel) return false;
        if (opDetails.flight || jobOrder?.operational_details?.flight || occurrence?.flight_details || occurrence?.flight) return false;

        // Check product_type from occurrence or snapshot
        const pTypeObj = occurrence?.product_type || occurrence?.productType || 
                         occurrence?.product_snapshot?.product_type || occurrence?.product_snapshot?.productType;
        const pTypeCode = (typeof pTypeObj === 'object' ? (pTypeObj?.code || pTypeObj?.name?.en || "") : String(pTypeObj || "")).toLowerCase();
        if (pTypeCode === "hotel" || pTypeCode === "flight") return false;
        if (pTypeCode === "tour" || pTypeCode === "transfer") return true;

        // Check category
        const catObj = occurrence?.category || occurrence?.product_snapshot?.category;
        const catCode = (typeof catObj === 'object' ? (catObj?.code || catObj?.name?.en || "") : String(catObj || "")).toLowerCase();
        if (catCode.includes("hotel") || catCode.includes("flight")) return false;
        if (catCode.includes("tour") || catCode.includes("transfer") || catCode.includes("transport") || catCode.includes("activity")) return true;

        // Check pricing code: per_person (tour) and per_unit (transfer)
        const pricingCode = (occurrence?.pricing_code || occurrence?.pricing_unit?.code || occurrence?.product_snapshot?.pricing_unit || "").toLowerCase();
        if (pricingCode === "per_person" || pricingCode === "per_unit") return true;
        if (pricingCode === "room_night" || pricingCode === "per_night" || pricingCode === "per_person_night") return false;

        // Check product title / service name
        const title = (
            occurrence?.displayProductName || 
            occurrence?.product_name || 
            occurrence?.title?.en || 
            occurrence?.title || 
            jobOrder?.service_name?.en || 
            ""
        ).toString().toLowerCase();
        if (title.includes("hotel") || title.includes("flight") || title.includes("airline")) return false;
        if (title.includes("transfer") || title.includes("tour") || title.includes("park") || title.includes("trip") || title.includes("safari") || title.includes("van")) return true;

        // Default: any general activity that is not hotel or flight gets locations
        return true;
    }, [occurrence, jobOrder]);

    useEffect(() => {
        if (open) {
            setNotes({
                ar: jobOrder?.notes?.ar || (typeof jobOrder?.notes === "string" ? jobOrder?.notes : "") || "",
                en: jobOrder?.notes?.en || ""
            });
            setPickupLocation({
                ar: jobOrder?.pickup_location?.ar || (typeof jobOrder?.pickup_location === "string" ? jobOrder?.pickup_location : "") || "",
                en: jobOrder?.pickup_location?.en || ""
            });
            setDropOffLocation({
                ar: jobOrder?.drop_off_location?.ar || (typeof jobOrder?.drop_off_location === "string" ? jobOrder?.drop_off_location : "") || "",
                en: jobOrder?.drop_off_location?.en || ""
            });
            setCashOnTour(jobOrder?.cash_on_tour !== undefined && jobOrder?.cash_on_tour !== null ? String(jobOrder.cash_on_tour) : "");

            if (isEdit && jobOrder?.pax_breakdown?.length > 0) {
                // Map from jobOrder pax_breakdown
                const breakdown = (program?.pax_types || []).map(pt => {
                    const found = jobOrder.pax_breakdown.find(b => b.pax_type_id === pt.pax_type_id || b.pax_type_id === pt.id);
                    return {
                        pax_type_id: pt.pax_type_id || pt.id,
                        name: pt.pax_type_name || pt.name || { ar: '', en: '' },
                        quantity: found ? found.quantity : 0
                    };
                });
                setPaxBreakdown(breakdown);
            } else {
                // Map from program
                const breakdown = (program?.pax_types || []).map(pt => ({
                    pax_type_id: pt.pax_type_id || pt.id,
                    name: pt.pax_type_name || pt.name || { ar: '', en: '' },
                    quantity: pt.count || pt.quantity || 0
                }));
                setPaxBreakdown(breakdown);
            }
        }
    }, [open, isEdit, jobOrder, program]);

    const handlePaxChange = (paxTypeId, value) => {
        const num = parseInt(value, 10);
        const quantity = isNaN(num) || num < 0 ? 0 : num;
        setPaxBreakdown(prev =>
            prev.map(p => p.pax_type_id === paxTypeId ? { ...p, quantity } : p)
        );
    };

    const handleSubmit = () => {
        if (isReadOnly) return;

        // Total quantity must be at least 1
        const totalPax = paxBreakdown.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);
        if (totalPax < 1) {
            toast.error(isArabic ? 'مجموع عدد الأفراد يجب أن يكون 1 على الأقل' : 'Total pax quantity must be at least 1');
            return;
        }

        const payload = {
            occurrence_key: occurrence?.occurrence_key,
            pax_breakdown: paxBreakdown.map(p => ({
                pax_type_id: p.pax_type_id,
                quantity: p.quantity
            }))
        };

        // Notes (available for all types)
        if (notes.ar?.trim() || notes.en?.trim()) {
            payload.notes = {
                ar: notes.ar?.trim() || "",
                en: notes.en?.trim() || ""
            };
        } else if (isEdit) {
            payload.notes = null;
        }

        // Locations (for tour & transfer)
        if (isTourOrTransfer) {
            if (pickupLocation.ar?.trim() || pickupLocation.en?.trim()) {
                payload.pickup_location = {
                    ar: pickupLocation.ar?.trim() || "",
                    en: pickupLocation.en?.trim() || ""
                };
            } else if (isEdit) {
                payload.pickup_location = null;
            }

            if (dropOffLocation.ar?.trim() || dropOffLocation.en?.trim()) {
                payload.drop_off_location = {
                    ar: dropOffLocation.ar?.trim() || "",
                    en: dropOffLocation.en?.trim() || ""
                };
            } else if (isEdit) {
                payload.drop_off_location = null;
            }
        }

        // Cash on tour (optional string: accepts text/numbers/currency)
        if (cashOnTour && cashOnTour.trim() !== "") {
            payload.cash_on_tour = cashOnTour.trim();
        } else if (isEdit) {
            payload.cash_on_tour = null;
        }

        if (isEdit) {
            updateJobOrderMutate({ jobOrderId: jobOrder.id, programId: program.id, data: payload }, {
                onSuccess: () => {
                    onSuccess();
                    onClose();
                }
            });
        } else {
            createJobOrderMutate({ programId: program.id, data: payload }, {
                onSuccess: () => {
                    onSuccess();
                    onClose();
                }
            });
        }
    };

    const serviceDisplayName = getLocalizedText(occurrence?.displayProductName, lng) || getLocalizedText(occurrence?.product_name, lng);
    const supplierDisplayName = getLocalizedText(occurrence?.supplier?.name, lng) || "-";

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {isReadOnly 
                    ? (isArabic ? "عرض أمر التشغيل" : "View Job Order")
                    : isEdit 
                        ? (isArabic ? "تعديل أمر التشغيل" : "Edit Job Order") 
                        : (isArabic ? "إنشاء أمر تشغيل وفاوتشر" : "Create Job Order & Voucher")}
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box mb={2}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        {isArabic ? "الخدمة" : "Service"}: {serviceDisplayName}
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary">
                        {isArabic ? "المورد" : "Supplier"}: {supplierDisplayName}
                    </Typography>
                    {isProgramSettled && (
                        <Typography variant="caption" color="warning.main" fontWeight="bold">
                            {isArabic ? "البرنامج مغلق (Settled)، الحقول متاحة للقراءة فقط." : "Program is settled. Fields are read-only."}
                        </Typography>
                    )}
                </Box>

                {!isEdit && (
                    <Box
                        sx={{
                            mb: 2.5,
                            p: 1.5,
                            bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(2, 136, 209, 0.12)" : "#f0f9ff"),
                            border: "1px solid #7dd3fc",
                            borderRadius: 1.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.2,
                        }}
                    >
                        <ReceiptLong sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: (theme) => (theme.palette.mode === "dark" ? "#bae6fd" : "#0369a1"), fontWeight: 500 }}>
                            {isArabic
                                ? "ملاحظة: سيتم إنشاء سند الخدمة (Job Voucher) تلقائيًا فور حفظ أمر التشغيل."
                                : "Note: A Job Voucher will be automatically created upon saving this Job Order."}
                        </Typography>
                    </Box>
                )}

                <Grid container spacing={2}>
                    {/* Cash on Tour (Job Number field removed per requirements as it is auto-generated) */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={isArabic ? "مبلغ التحصيل بالجولة (Cash on Tour)" : "Cash on Tour"}
                            value={cashOnTour}
                            onChange={(e) => setCashOnTour(e.target.value)}
                            disabled={isReadOnly}
                            variant="outlined"
                            placeholder="e.g. 1500 THB / Paid to driver"
                            helperText={isArabic ? "اختياري، نص يقبل أرقاماً ورموزاً، لا يدخل في سعر البرنامج أو الفاتورة" : "Optional, accepts text & numbers, does not affect program price"}
                        />
                    </Grid>

                    {/* Locations for Tour and Transfer only (appears in both create and edit) */}
                    {isTourOrTransfer && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={isArabic ? "مكان الاستقبال (عربي)" : "Pickup Location (Arabic)"}
                                    value={pickupLocation.ar}
                                    onChange={(e) => setPickupLocation(prev => ({ ...prev, ar: e.target.value }))}
                                    disabled={isReadOnly}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={isArabic ? "مكان الاستقبال (إنجليزي)" : "Pickup Location (English)"}
                                    value={pickupLocation.en}
                                    onChange={(e) => setPickupLocation(prev => ({ ...prev, en: e.target.value }))}
                                    disabled={isReadOnly}
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={isArabic ? "مكان التوصيل (عربي)" : "Drop-off Location (Arabic)"}
                                    value={dropOffLocation.ar}
                                    onChange={(e) => setDropOffLocation(prev => ({ ...prev, ar: e.target.value }))}
                                    disabled={isReadOnly}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={isArabic ? "مكان التوصيل (إنجليزي)" : "Drop-off Location (English)"}
                                    value={dropOffLocation.en}
                                    onChange={(e) => setDropOffLocation(prev => ({ ...prev, en: e.target.value }))}
                                    disabled={isReadOnly}
                                    variant="outlined"
                                />
                            </Grid>
                        </>
                    )}

                    {/* Notes for all types */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={isArabic ? "ملاحظات التشغيل (عربي)" : "Operational Notes (Arabic)"}
                            value={notes.ar}
                            onChange={(e) => setNotes(prev => ({ ...prev, ar: e.target.value }))}
                            disabled={isReadOnly}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={isArabic ? "ملاحظات التشغيل (إنجليزي)" : "Operational Notes (English)"}
                            value={notes.en}
                            onChange={(e) => setNotes(prev => ({ ...prev, en: e.target.value }))}
                            disabled={isReadOnly}
                            variant="outlined"
                        />
                    </Grid>
                </Grid>

                <Box mt={3}>
                    <Typography variant="h6" mb={2}>
                        {isArabic ? "توزيع الأعداد" : "Pax Breakdown"}
                    </Typography>
                    <Grid container spacing={2}>
                        {paxBreakdown.map((pax) => (
                            <Grid item xs={6} sm={4} key={pax.pax_type_id}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={getLocalizedText(pax.name, lng)}
                                    value={pax.quantity}
                                    onChange={(e) => handlePaxChange(pax.pax_type_id, e.target.value)}
                                    disabled={isReadOnly}
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isCreating || isUpdating}>
                    {isReadOnly ? (isArabic ? "إغلاق" : "Close") : (isArabic ? "إلغاء" : "Cancel")}
                </Button>
                {!isReadOnly && (
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={isCreating || isUpdating}
                    >
                        {isEdit ? (isArabic ? "حفظ" : "Save") : (isArabic ? "إنشاء" : "Create")}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CreateEditJobOrderModal;
